import {
  type FetchProductListParams,
  fetchProducts,
  fetchSingleProduct,
  fetchTariffCatalogProducts,
  fetchTariffProducts
} from 'Services/ProductManagement/Features/Product/productSlice'
import { getProductsInTariffParamKey } from 'Services/ProductManagement/Features/Product/utils'
import type { FetchListParams } from 'Services/ProductManagement/types'
import useTypedSelector, { useAppDispatch } from 'Store'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseProductsWithParamsProps {
  hasNoTariff?: boolean
  inTariff?: string
  inCatalog?: string
}

export const SELECTED_PRODUCT_PARAMETER_KEY = 'selectedProduct'
const defaultSortByKey = 'productCode'
const defaultSortOrder = SORT_ORDER_ASC
const defaultCount = ROWS_PER_PAGE_DEFAULT

function useProductsWithParams({ hasNoTariff, inTariff, inCatalog }: UseProductsWithParamsProps) {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    products,
    loading,
    tariffProducts,
    tariffProductsLoading,
    tariffCatalogProducts,
    tariffCatalogProductsLoading,
    next_page,
    previous_page,
    total_results
  } = useTypedSelector(state => state.product)

  const [searchQueryParams, setSearchQueryParams] = useState<FetchListParams>({
    filter: searchParams.get('filter') ?? undefined,
    search_text: searchParams.get('search_text') ?? undefined,
    sort_by: searchParams.get('sort_by') ?? defaultSortByKey,
    sort_order: searchParams.get('sort_order') ?? defaultSortOrder,
    start_index: searchParams.get('start_index') ?? undefined,
    count: defaultCount
  })

  const params: FetchProductListParams = {
    ...searchQueryParams,
    has_no_tariff: hasNoTariff,
    [getProductsInTariffParamKey(inCatalog)]: inTariff,
    in_catalog: inCatalog
  }

  const selectedProductQueryParam = searchParams.get(SELECTED_PRODUCT_PARAMETER_KEY) ?? undefined
  const [selectedProductId, setSelectedProductId] = useState(selectedProductQueryParam)

  const setQueryParams = () => {
    Object.entries(searchQueryParams).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    // Set selectedProductId if it exists
    selectedProductId && searchParams.set(SELECTED_PRODUCT_PARAMETER_KEY, selectedProductId)

    setSearchParams(searchParams)
  }

  const resetQueryParams = () => {
    setSearchQueryParams({
      filter: undefined,
      search_text: undefined,
      sort_by: defaultSortByKey,
      sort_order: defaultSortOrder,
      start_index: undefined,
      count: defaultCount
    })
  }

  useEffect(() => {
    if (inCatalog) {
      dispatch(fetchTariffCatalogProducts(params))
    } else if (inTariff) {
      dispatch(fetchTariffProducts(params))
    } else {
      dispatch(fetchProducts(params))
    }
    setQueryParams()
  }, [inTariff, inCatalog, hasNoTariff, searchQueryParams])

  useEffect(() => {
    if (selectedProductId) {
      dispatch(fetchSingleProduct({ id: selectedProductId }))
      setQueryParams()
    } else {
      searchParams.delete(SELECTED_PRODUCT_PARAMETER_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedProductId])

  const fetchTariffProductsWithParams = () => {
    dispatch(fetchTariffProducts(params))
    setQueryParams()
  }

  const fetchTariffProductsWithParamsAfterDeleteProduct = (
    productParams: FetchProductListParams
  ) => {
    dispatch(fetchTariffProducts(productParams))
    setQueryParams()
    searchParams.delete(SELECTED_PRODUCT_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const fetchTariffCatalogProductsWithParams = () => {
    dispatch(fetchTariffCatalogProducts(params))
    setQueryParams()
  }

  const fetchProductsWithParams = () => {
    dispatch(fetchProducts(params))
    setQueryParams()
  }

  return {
    products,
    loading,
    tariffProducts,
    tariffProductsLoading,
    tariffCatalogProducts,
    tariffCatalogProductsLoading,
    searchQueryParams,
    setSearchQueryParams,
    total_results,
    next_page,
    previous_page,
    selectedProductId,
    setSelectedProductId,
    fetchTariffProductsWithParams,
    fetchTariffCatalogProductsWithParams,
    fetchProductsWithParams,
    fetchTariffProductsWithParamsAfterDeleteProduct,
    resetQueryParams
  }
}

export default useProductsWithParams
